import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarDays,
  ChevronRight,
  Star,
  Clock,
  MapPin,
  Phone,
  Shield,
  BookOpen,
  Navigation,
} from 'lucide-react';
import { PublicLayout } from '@/components/PublicLayout';
import { useSettings } from '@/contexts/SettingsContext';
import { api } from '@/lib/api';
import { TID } from '@/lib/testIds';
import { ReviewCard, StarRating } from '@/components/ReviewCard';
import { FeaturedCarousel } from '@/components/FeaturedCarousel';
import { SafeImage } from '@/components/SafeImage';
import { DeliveryMenu } from '@/components/DeliveryMenu';
import { PUBLIC_MENU_ENABLED, PUBLIC_TASTE_ENABLED } from '@/lib/launchMode';

const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

// Uber Eats brand-consistent monochrome mark (avoids fetching remote SVG assets)
const UberEatsMark = ({ className = 'h-4' }) => (
  <span className={`font-bold tracking-tight ${className}`} style={{ letterSpacing: '-0.02em' }}>
    Uber <span className="text-[hsl(var(--sb-green))]">Eats</span>
  </span>
);
const DeliverooMark = ({ className = 'h-4' }) => (
  <span className={`font-bold tracking-tight ${className}`} style={{ color: '#00CCBC' }}>
    deliveroo
  </span>
);
const JustEatMark = ({ className = 'h-4' }) => (
  <span className={`font-bold tracking-tight ${className}`} style={{ color: '#FF8000' }}>
    Just Eat
  </span>
);

export default function HomePage() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, total: 0 });
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get('/menu/featured').then(({ data }) => setFeatured(data || [])).catch(() => {});
    api.get('/reviews').then(({ data }) => setReviews(data || { reviews: [], average: 0, total: 0 })).catch(() => {});
    api.get('/gallery').then(({ data }) => setGallery((data || []).slice(0, 4))).catch(() => {});
  }, []);

  const heroImage = useMemo(() => {
    // Distinguish "still loading" (undefined) from "loaded but no image" (null).
    // Prevents SafeImage from flashing the branded fallback on first paint before
    // /api/menu/featured has resolved.
    if (!featured.length) return undefined;
    const first = featured.find((f) => !!f.image_url);
    return first?.image_url || null;
  }, [featured]);

  const social = settings?.social_links || {};
  const directionsHref =
    social.google_directions ||
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings?.address || 'Smithy Browns Milton Keynes')}`;

  const deliveryPartners = [
    { key: 'uber_eats', label: 'Uber Eats', href: social.uber_eats, Mark: UberEatsMark, testId: TID.home.deliveryUber },
    { key: 'deliveroo', label: 'Deliveroo', href: social.deliveroo, Mark: DeliverooMark, testId: TID.home.deliveryDeliveroo },
    { key: 'just_eat', label: 'Just Eat', href: social.just_eat, Mark: JustEatMark, testId: TID.home.deliveryJustEat },
  ].filter((p) => !!p.href);

  return (
    <PublicLayout>
      {/* ===== Hero ===== */}
      <section data-testid={TID.home.hero} className="relative sb-hero-bg overflow-hidden">
        <div className="sb-grain absolute inset-0" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-12 sm:pt-16 sm:pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-10 items-center relative">
          <div>
            {/* Mobile-only prominent Order for Delivery CTA — appears above the
                eyebrow so it is visible without scrolling on landing. Desktop
                already surfaces the same action in the header. Uses the shared
                DeliveryMenu (single source of truth via settings.social_links). */}
            <div className="sm:hidden mb-5">
              <DeliveryMenu variant="hero-mobile" />
            </div>
            <div className="inline-flex items-center gap-2 text-[hsl(var(--sb-gold))] text-[11px] uppercase tracking-widest mb-4">
              <Star size={12} fill="currentColor" /> Milton Keynes&rsquo; First Authentic Jamaican Restaurant
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-white"
            >
              AUTHENTIC FLAVOURS<br />
              <span className="sb-text-gradient-gold">TO SLAP YOUR TASTE BUDS</span>
            </motion.h1>
            <p className="mt-4 text-white/75 text-base sm:text-lg max-w-md">
              Slow-cooked oxtail, jerk fire-grilled chicken, and sticky Hennessy wings — the real Jamaican experience, served fresh in Bletchley.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              {PUBLIC_MENU_ENABLED && (
                <Link
                  to="/menu"
                  data-testid={TID.home.menuBtn}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] transition-colors active:scale-[0.98]"
                >
                  <BookOpen size={18} /> View Menu <ChevronRight size={16} />
                </Link>
              )}
              <Link
                to="/book"
                data-testid={TID.home.bookBtn}
                className={`inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold transition-colors active:scale-[0.98] ${
                  PUBLIC_MENU_ENABLED
                    ? 'bg-[hsl(var(--sb-surface-2))] border border-[hsl(var(--sb-border))] text-white hover:border-[hsl(var(--sb-gold))]/60'
                    : 'bg-[hsl(var(--sb-gold))] text-black hover:bg-[hsl(var(--sb-gold-2))]'
                }`}
              >
                <CalendarDays size={18} /> Book a Table {!PUBLIC_MENU_ENABLED && <ChevronRight size={16} />}
              </Link>
              <a
                href={directionsHref}
                target="_blank"
                rel="noreferrer"
                data-testid={TID.home.directionsBtn}
                className={`inline-flex items-center gap-2 h-12 px-6 rounded-full transition-colors ${
                  PUBLIC_MENU_ENABLED
                    ? 'border border-[hsl(var(--sb-border))] text-white/85 hover:text-white hover:border-white/40'
                    : 'bg-[hsl(var(--sb-surface-2))] border border-[hsl(var(--sb-border))] text-white hover:border-[hsl(var(--sb-gold))]/60'
                }`}
              >
                <Navigation size={16} /> Get Directions
              </a>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-widest text-white/70">
              <div className="flex items-center gap-2"><Shield size={14} className="text-[hsl(var(--sb-green))]" /> Halal Certified</div>
              <div className="flex items-center gap-2"><Clock size={14} className="text-[hsl(var(--sb-gold))]" /> Fresh Daily</div>
              {reviews.total > 0 && (
                <div className="flex items-center gap-2">
                  <StarRating value={reviews.average} size={12} />
                  <span>{reviews.average}/5 · {reviews.total} reviews</span>
                </div>
              )}
            </div>
          </div>

          {/* Hero food image — real dish first, tasteful Jamaican food fallback second, branded silhouette last */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative w-full max-w-md mx-auto"
          >
            <div className="relative aspect-[5/6] sm:aspect-square lg:aspect-[5/6] rounded-2xl overflow-hidden sb-gold-glow border border-[hsl(var(--sb-border))]">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[hsl(var(--sb-green))]/25 blur-3xl z-0" />
              <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-[hsl(var(--sb-gold))]/25 blur-3xl z-0" />
              {/*
                Phase 11.2.1 — full-bleed cover crop. Fallback chain: real Menu Item
                image → branded Smithy Browns SafeImage silhouette. No Unsplash /
                stock image fallback (removed to eliminate placeholder flash on
                first paint).
              */}
              <SafeImage
                src={heroImage}
                alt="Signature Smithy Browns Jamaican dish"
                className="absolute inset-0 w-full h-full z-10"
                imgClassName="absolute inset-0 w-full h-full object-cover object-[50%_45%] sm:object-[50%_50%] lg:object-[50%_42%]"
                fallbackIconSize={48}
              />
              {/* Subtle inner vignette — adds premium depth without letterboxing */}
              <div className="absolute inset-0 z-20 pointer-events-none" style={{ boxShadow: 'inset 0 -80px 80px -30px rgba(0,0,0,0.55), inset 0 40px 60px -30px rgba(0,0,0,0.35)' }} />
              {/* Bottom gradient for overlay legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none z-20" />
              <div className="absolute bottom-4 left-4 right-4 z-30 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/85">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold">
                  <Star size={10} fill="currentColor" /> Signature
                </span>
                <span className="truncate">Made fresh in Bletchley</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== Signature Dishes Carousel ===== */}
      {/*
        Retained on the customer homepage during Launch Mode as a curated preview of
        signature dishes (no link to the full menu — that CTA is gated inside the
        card itself). Data source: /api/menu/featured, driven by
        Admin → Menu Items → Homepage display → Show as Featured Dish.
      */}
      <div data-testid={TID.home.featured}>
        <FeaturedCarousel items={featured} />
      </div>

      {/* ===== External Delivery CTAs strip ===== */}
      {deliveryPartners.length > 0 && (
        <section
          data-testid={TID.home.delivery}
          className="border-y border-[hsl(var(--sb-border))] bg-black/40"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))] grid place-items-center">
                <BookOpen size={16} />
              </div>
              <div>
                <div className="text-[hsl(var(--sb-gold))] uppercase text-[10px] tracking-widest">Order for delivery</div>
                <div className="font-display text-lg sm:text-xl text-white leading-tight">Get Smithy Browns Delivered</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {deliveryPartners.map(({ key, label, href, Mark, testId }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={testId}
                  aria-label={`Order on ${label}`}
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white text-black hover:bg-white/90 transition-colors border border-white/10 shadow-sm active:scale-[0.98]"
                >
                  <Mark />
                  <ChevronRight size={14} />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== A Taste of This Place ===== */}
      {/* Independent visibility flag — kept hidden until Smithy Browns are ready to
          reveal the gallery. Admin config on `/admin/taste` remains untouched. */}
      {PUBLIC_TASTE_ENABLED && gallery.length > 0 && (
        <section
          data-testid={TID.home.gallery}
          className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12"
        >
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest">Inside Smithy Browns</div>
              <h2 className="font-display text-3xl sm:text-4xl text-white mt-1">A TASTE OF THE PLACE</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 grid-rows-2 gap-2 sm:gap-3 h-[380px] sm:h-[440px] lg:h-[480px]">
            {gallery.slice(0, 4).map((g, idx) => {
              // 4-col × 2-row grid = 8 cells total.
              // 0: large (2×2), 1: wide (2×1), 2: small (1×1), 3: small (1×1)
              const spanClass =
                idx === 0
                  ? 'col-span-2 row-span-2'
                  : idx === 1
                  ? 'col-span-2 row-span-1'
                  : 'col-span-1 row-span-1 hidden sm:block';
              return (
                <div
                  key={g.id}
                  className={`relative overflow-hidden rounded-md group ${spanClass}`}
                >
                  <SafeImage
                    src={g.image_url}
                    alt={g.caption || 'Smithy Browns'}
                    className="absolute inset-0 h-full w-full"
                    imgClassName="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                  />
                  {g.caption && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3 flex items-end">
                      <span className="text-xs text-white">{g.caption}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ===== Google Reviews ===== */}
      {reviews.reviews.length > 0 && (
        <section
          data-testid={TID.home.reviews}
          className="bg-black/30 border-b border-[hsl(var(--sb-border))]"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest">Reviewed on Google</div>
                <h2 className="font-display text-3xl sm:text-4xl text-white mt-1">WHAT OUR CUSTOMERS SAY</h2>
              </div>
              <a
                href={social.google_reviews || 'https://www.google.com/search?q=Smithy+Browns+Milton+Keynes'}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))]"
              >
                <Star size={14} fill="currentColor" /> Leave a Review
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.reviews.slice(0, 6).map((r) => (
                <ReviewCard key={r.id} review={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== Location + Opening Hours (combined) ===== */}
      <section
        data-testid={TID.home.hours}
        className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-12 grid md:grid-cols-2 gap-6"
      >
        <div className="sb-card p-6">
          <div className="flex items-center gap-2 text-[hsl(var(--sb-gold))]">
            <Clock size={18} />
            <span className="uppercase text-xs tracking-widest">Opening Hours</span>
          </div>
          <h3 className="font-display text-2xl text-white mt-2">WHEN WE&rsquo;RE OPEN</h3>
          <ul className="mt-4 divide-y divide-[hsl(var(--sb-border))]">
            {(settings?.opening_hours || []).map((h) => (
              <li key={h.day} className="flex justify-between py-2 text-sm">
                <span className="text-white/85">{DAY_LABEL[h.day]}</span>
                <span className={h.closed ? 'text-[hsl(var(--sb-red))]' : 'text-white'}>
                  {h.closed ? 'Closed' : `${h.open} – ${h.close}`}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="sb-card p-6 flex flex-col">
          <div className="flex items-center gap-2 text-[hsl(var(--sb-gold))]">
            <MapPin size={18} />
            <span className="uppercase text-xs tracking-widest">Find Us</span>
          </div>
          <h3 className="font-display text-2xl text-white mt-2">YOU CAN&rsquo;T MISS IT</h3>
          <p className="text-white/75 text-sm mt-2">{settings?.address}</p>
          <div className="text-white/75 text-sm mt-1 flex items-center gap-2">
            <Phone size={14} className="text-[hsl(var(--sb-gold))]" /> {settings?.phone}
          </div>
          <div className="mt-4 aspect-video w-full rounded-md overflow-hidden border border-[hsl(var(--sb-border))]">
            {settings?.map_embed_url && (
              <iframe
                title="Smithy Browns Map"
                src={settings.map_embed_url}
                className="w-full h-full"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            )}
          </div>
          <a
            href={directionsHref}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] transition-colors self-start"
          >
            <Navigation size={16} /> Get Directions
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
